import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import {
  Security as SecurityIcon,
  ExitToApp as ExitIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';// import { logger } from "../../utils/logger";
interface ImpersonationStatusProps {
  variant?: 'compact' | 'full';
  showExitButton?: boolean;
}

const ImpersonationStatus: React.FC<ImpersonationStatusProps> = ({ 
  variant = 'compact',
  showExitButton = true 
}) => {
  const { stopImpersonation, user } = useAuth();
  
  // Check if user is in impersonation mode from session data
  const isInImpersonationMode = user?.is_impersonation === true;

  if (!isInImpersonationMode) {
    return null;
  }

  const handleExitImpersonation = async () => {
    if (confirm('Are you sure you want to exit impersonation mode? You will be redirected back to the admin panel.')) {
      await stopImpersonation();
    }
  };

  // Get impersonated user info for display
  const impersonatedUserName = user?.first_name && user?.last_name 
    ? `${user.first_name} ${user.last_name}` 
    : user?.email || 'User';

  if (variant === 'compact') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={`Impersonating: ${impersonatedUserName}`}>
          <Chip
            icon={<SecurityIcon />}
            label={`Impersonating: ${impersonatedUserName}`}
            size="small"
            color="warning"
            variant="outlined"
            sx={{ 
              fontSize: '0.75rem',
              height: 24,
              maxWidth: 200,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              },
              '& .MuiChip-icon': { fontSize: '0.875rem' }
            }}
          />
        </Tooltip>
        {showExitButton && (
          <Tooltip title="Exit Impersonation Mode">
            <IconButton
              size="small"
              onClick={handleExitImpersonation}
              aria-label="Exit impersonation mode"
              sx={{ 
                color: 'warning.main',
                p: 0.5,
                '&:hover': { backgroundColor: 'warning.light', color: 'warning.contrastText' }
              }}
            >
              <ExitIcon fontSize="small" aria-hidden />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  // Full variant
  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1,
      p: 1,
      borderRadius: 1,
      backgroundColor: 'warning.light',
      border: '1px solid',
      borderColor: 'warning.main'
    }}>
      <WarningIcon color="warning" fontSize="small" />
      <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 500 }}>
        Impersonating: {impersonatedUserName}
      </Typography>
      {showExitButton && (
        <Button
          size="small"
          variant="outlined"
          color="warning"
          startIcon={<ExitIcon />}
          onClick={handleExitImpersonation}
          sx={{ 
            ml: 'auto',
            fontSize: '0.75rem',
            py: 0.25,
            px: 1,
            minWidth: 'auto'
          }}
        >
          Exit
        </Button>
      )}
    </Box>
  );
};

export default ImpersonationStatus;
